/**
 * AuthLoginPage — shared sign-in UI for all Discovr apps.
 *
 * Two layouts from one component:
 *  - Marketing split (Figma "Screen / Login"): pass `headline` + `sidePanel`
 *    → left form column with eyebrow/headline/subheadline, right visual panel.
 *  - Centered card (admin / fallback): omit `sidePanel` → simple centered form.
 *
 * The parent owns the Firebase call and passes the result via `onSubmit`.
 * App-specific extras (waitlist CTA, legal links) come in via props so this
 * component stays free of app-specific imports.
 */
import React, { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { CircleNotch, Eye, EyeSlash, Info, WarningCircle } from '../../design-system/icons';
import { Alert } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DiscovrLogo } from '../../design-system/components/discovr-logo';
import { AuthSplitLayout, type AuthEyebrowIcon } from './AuthSplitLayout';
import { GoogleGlyph } from './GoogleGlyph';

export interface AuthLoginPageProps {
  /** Called when the form is submitted. Parent owns the auth logic. */
  onSubmit: (email: string, password: string) => Promise<void>;
  error?: string | null;
  isLoading?: boolean;
  appName?: string;
  /** Alert shown above the form (session expired, access denied, etc.). */
  alert?: { type: 'info' | 'warning' | 'error'; message: string } | null;
  showForgotPassword?: boolean;
  showLegalLinks?: boolean;
  /** Waitlist CTA / app-specific call-to-action rendered below the button. */
  bottomSlot?: React.ReactNode;
  /** Called when the user clicks "Continue with Google". Omit to hide the button. */
  onGoogleSignIn?: () => Promise<void>;
  googleLoading?: boolean;
  /**
   * Called when the user clicks "Email me a code instead", with whatever
   * email they've already typed (may be empty) so the caller can carry it
   * forward as a prefill. Omit to hide the link.
   */
  onOtpSignIn?: (email: string) => void;
  /** Pre-filled credentials (e.g. the try.getdiscovr.ai demo login). Read on mount only. */
  initialEmail?: string;
  initialPassword?: string;

  // ── Marketing split layout (Figma) ──────────────────────────────────────────
  /** Small uppercase eyebrow above the headline (e.g. "Welcome back"). */
  eyebrow?: string;
  eyebrowIcon?: AuthEyebrowIcon;
  /** Big marketing headline. When provided, renders the marketing layout. */
  headline?: React.ReactNode;
  subheadline?: string;
  /** Right-hand visual panel. When provided, renders the two-column split. */
  sidePanel?: React.ReactNode;
}

export function AuthLoginPage({
  onSubmit,
  error,
  isLoading = false,
  appName = 'Discovr',
  alert,
  showForgotPassword = false,
  showLegalLinks = false,
  bottomSlot,
  onGoogleSignIn,
  googleLoading = false,
  onOtpSignIn,
  initialEmail = '',
  initialPassword = '',
  eyebrow,
  eyebrowIcon,
  headline,
  subheadline,
  sidePanel,
}: AuthLoginPageProps) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(initialPassword);
  const [showPassword, setShowPassword] = useState(false);
  const split = !!sidePanel;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    await onSubmit(email, password);
  };

  const alternateSignInSection = (onGoogleSignIn || onOtpSignIn) && (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-caption-regular text-muted-foreground">or</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      {onGoogleSignIn && (
        <Button
          type="button"
          variant="outline"
          onClick={() => { void onGoogleSignIn(); }}
          disabled={isLoading || googleLoading}
          className="h-11 w-full rounded-full border-border text-body-md-medium text-foreground hover:bg-muted disabled:opacity-50"
        >
          {googleLoading ? <CircleNotch size={16} className="mr-2 animate-spin" /> : <GoogleGlyph size={16} className="mr-2" />}
          Continue with Google
        </Button>
      )}
      {onOtpSignIn && (
        <button
          type="button"
          onClick={() => onOtpSignIn(email)}
          disabled={isLoading}
          className="text-center text-body-sm-medium text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        >
          Email me a code instead
        </button>
      )}
    </div>
  );

  const form = (
    <div className="flex flex-col gap-6">
      <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-6">
      {alert && (
        <Alert
          variant={alert.type === 'error' ? 'destructive' : alert.type === 'info' ? 'info' : 'warning'}
          role={alert.type === 'info' ? 'status' : undefined}
        >
          {alert.type === 'info' ? (
            <Info aria-hidden="true" />
          ) : (
            <WarningCircle />
          )}
          <span>{alert.message}</span>
        </Alert>
      )}

      {/* Inputs sub-group: email + password at gap-4 (Figma Frame 41 gap=16) */}
      <div className="flex flex-col gap-4">
        {/* Email */}
        <div className="flex flex-col gap-1">
          <label htmlFor="auth-email" className="text-body-sm-medium text-foreground">
            Work email
          </label>
          <Input
            id="auth-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            required
            className="h-11 rounded-lg border-border bg-card"
          />
        </div>

        {/* Password */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <label htmlFor="auth-password" className="text-body-sm-medium text-foreground">
              Password
            </label>
            {showForgotPassword && (
              <Link to="/forgot-password" className="text-caption-regular text-muted-foreground hover:text-foreground">
                Forgot password?
              </Link>
            )}
          </div>
          <div className="relative">
            <Input
              id="auth-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              className="h-11 rounded-lg border-border bg-card pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-foreground hover:text-foreground"
            >
              {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-700">
          <WarningCircle size={15} className="mt-px shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={isLoading || !email.trim() || !password}
        className="h-11 w-full rounded-full bg-primary text-body-md-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {isLoading ? (
          <>
            <CircleNotch size={16} className="mr-2 animate-spin" />
            Signing in…
          </>
        ) : headline ? (
          `Login to ${appName}`
        ) : (
          'Sign in'
        )}
      </Button>
      </form>
      {alternateSignInSection}
    </div>
  );

  const legal = showLegalLinks && (
    <p className="text-center text-caption-regular text-(--icon-subtle)">
      By signing in, you agree to our{' '}
      <Link to="/terms" className="text-stone-700 hover:text-foreground">Terms</Link>
      {' '}and{' '}
      <Link to="/privacy-policy" className="text-stone-700 hover:text-foreground">Privacy Policy</Link>
    </p>
  );

  const footer = bottomSlot || legal ? (
    <>
      {bottomSlot}
      {legal}
    </>
  ) : null;

  // ── Split (marketing) layout (Figma) ────────────────────────────────────────
  if (split) {
    return (
      <AuthSplitLayout
        eyebrow={eyebrow}
        eyebrowIcon={eyebrowIcon}
        headline={headline}
        subheadline={subheadline}
        sidePanel={sidePanel}
        footer={footer}
      >
        {form}
      </AuthSplitLayout>
    );
  }

  // ── Centered card (admin / fallback) ────────────────────────────────────────
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-12">
      <div className="animate-in fade-in zoom-in-95 flex w-full max-w-[400px] flex-col gap-6 duration-300">
        <div className="flex flex-col items-center gap-3">
          <DiscovrLogo size={28} />
          <p className="text-body-sm-regular text-muted-foreground">Sign in to continue to {appName}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-8">{form}</div>
        {footer && <div className="flex flex-col gap-4">{footer}</div>}
      </div>
    </div>
  );
}
